import {getTelehealthStepPosition} from 'presentation/modules/booking/telehealth/utils/getTelehealthStepPosition';
import {UseTelehealthSearchBarProps} from './types';
import {useGetHealthProvidersNameQuery} from 'presentation/modules/booking/shared/queries/useGetHealthProvidersNameQuery';
import {useProviderNamesStore} from 'presentation/modules/booking/shared/store/useProviderNamesStore';
import {useGetHealthDatesQuery} from 'presentation/modules/booking/shared/queries/useGetHealthDatesQuery';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useOpenDrawerFilterStore} from 'presentation/modules/booking/shared/store/useOpenDrawerFilterStore';
import {format, parse} from 'date-fns';
import {ptBR} from 'date-fns/locale';
import {QueryParamsEnum} from 'presentation/constants';
import {useQueryParams} from 'presentation/pages';
import {useExternalOpenDateModalStore} from 'presentation/modules/booking/shared/store/useExternalOpenDateModalStore';
import {useDisabledSelectionDateStore} from 'presentation/modules/booking/shared/store/useDisabledSelectionDateStore';
import {ShiftTypeEnum} from 'presentation/modules/booking/exam/constants/shiftTypeEnum';
import {useState} from 'react';
import {createPeriodsConfig} from 'presentation/modules/booking/shared/utils/createPeriodsConfig';

export function useTelehealthSearchBar({activeStep}: UseTelehealthSearchBarProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const setOpenDrawerState = useOpenDrawerFilterStore(state => state.setOpenDrawerState);
	const openDrawerState = useOpenDrawerFilterStore(state => state.openDrawerState);
	const getProviderNamesState = useProviderNamesStore(state => state.providersName);
	const externalOpenDateModal = useExternalOpenDateModalStore(
		state => state.externalOpenDateModal,
	);
	const setExternalOpenDateModal = useExternalOpenDateModalStore(
		state => state.setExternalOpenDateModal,
	);
	const disabledSelectionDate = useDisabledSelectionDateStore(
		state => state.disabledSelectionDate,
	);

	const {
		SEARCH_PARAMS: {shift, query, autocompleteQuery},
		setSearchParams,
		clearSearch,
	} = useQueryParams();
	const [shiftSelected, setShiftSelected] = useState<ShiftTypeEnum>(
		(shift as ShiftTypeEnum) ?? ShiftTypeEnum.ALL,
	);

	const {PROFESSIONAL_STEP, SPECIALITY_OR_EXAM_STEP} = getTelehealthStepPosition();

	const {dateAt} = bookingData;

	const {isError: providersNameHasError, isFetching: providersNameIsLoading} =
		useGetHealthProvidersNameQuery({enabled: activeStep === PROFESSIONAL_STEP});

	const {
		data: healthAvailableDatesList,
		isFetching: healthDatesIsLoading,
		isError: healthDatesIsError,
	} = useGetHealthDatesQuery({
		enabled: activeStep === PROFESSIONAL_STEP,
		isTelemedicine: true,
	});

	const providersNameOptions =
		getProviderNamesState?.medicos?.map(medico => ({
			id: medico?.cdPrestadorFisico,
			label: medico?.nmMedico,
		})) ?? [];

	const shouldRenderAutocomplete =
		activeStep === PROFESSIONAL_STEP && !providersNameHasError && !!getProviderNamesState;

	const isGettingProviderNames = providersNameIsLoading || healthDatesIsLoading;

	const showProfessionalFilters = activeStep === PROFESSIONAL_STEP;

	const currentSelectedDate = dateAt ? parse(dateAt, 'P', 0, {locale: ptBR}) : undefined;

	const availableDatesLoadingOrError = healthDatesIsLoading || healthDatesIsError;
	const availableDates = availableDatesLoadingOrError ? [] : healthAvailableDatesList;

	const showFilterByLetter = !query && activeStep === SPECIALITY_OR_EXAM_STEP;

	const PERIODS = createPeriodsConfig({
		shiftSelected,
		onShiftChange: (shift: ShiftTypeEnum) => setShiftSelected(shift),
	});

	const handleOpenDrawer = () => {
		setOpenDrawerState(true);
	};

	const handleChangeDate = (selectedDate: Date | null) => {
		setSearchParams(prev => {
			if (selectedDate) {
				setBookingData(prevValue => ({
					...prevValue,
					dateAt: format(selectedDate, 'P', {locale: ptBR}),
				}));

				prev.set(QueryParamsEnum.CALENDAR_ACTIVE, 'true');
			} else {
				setBookingData(prevValue => ({
					...prevValue,
					dateAt: '',
				}));

				prev.delete(QueryParamsEnum.CALENDAR_ACTIVE);
			}

			return prev;
		});
		setExternalOpenDateModal(false);
	};

	const handleCloseDrawer = (abandon?: boolean) => {
		setOpenDrawerState(false);
		if (abandon) {
			setShiftSelected(ShiftTypeEnum.ALL);
		}
	};

	const handleShiftButton = (shiftType: ShiftTypeEnum | string) => {
		setSearchParams(
			prev => {
				prev.set(QueryParamsEnum.SHIFT, shiftType);
				return prev;
			},
			{
				replace: true,
			},
		);
	};

	const handleClearShift = () => {
		setShiftSelected(ShiftTypeEnum.ALL);
		handleShiftButton(ShiftTypeEnum.ALL);
		handleCloseDrawer();
	};

	const handleConfirmShift = () => {
		handleShiftButton(shiftSelected);
		handleCloseDrawer();
	};

	const clearSearchParams = () => {
		setSearchParams(
			prev => {
				prev.delete(QueryParamsEnum.SEARCH_QUERY);
				prev.delete(QueryParamsEnum.AUTOCOMPLETE_QUERY);
				prev.delete(QueryParamsEnum.SEARCH_BY_LETTER);

				return prev;
			},
			{
				replace: true,
			},
		);
	};

	const handleClearShiftAndSearch = () => {
		setShiftSelected(ShiftTypeEnum.ALL);
		handleShiftButton(ShiftTypeEnum.ALL);
		handleCloseDrawer();
		clearSearchParams();
		if (clearSearch) {
			clearSearch();
		}
		setSearchParams(prev => {
			setBookingData(prevValue => ({
				...prevValue,
				dateAt: '',
			}));

			prev.set(QueryParamsEnum.SEARCH_QUERY, 'clear');
			prev.delete(QueryParamsEnum.CALENDAR_ACTIVE);
			if (autocompleteQuery) prev.delete(QueryParamsEnum.AUTOCOMPLETE_QUERY);
			return prev;
		});
	};

	return {
		shouldRenderAutocomplete,
		isGettingProviderNames,
		providersNameOptions,
		handleOpenDrawer,
		showProfessionalFilters,
		currentSelectedDate,
		handleChangeDate,
		availableDates,
		shift,
		disabledSelectionDate,
		externalOpenDateModal,
		setExternalOpenDateModal,
		openDrawerState,
		handleCloseDrawer,
		handleClearShift,
		handleConfirmShift,
		PERIODS,
		showFilterByLetter,
		handleClearShiftAndSearch,
	};
}
